if (!customElements.get('lookbook-icon')) {
  customElements.define(
    'lookbook-icon',
    class LookbookIcon extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          cardContainer: '.lookbook-card',
          cardProduct: '.lookbook-icon__card',
          closeButton: '.lookbook-icon__close',
          pin: '.lookbook-icon__pin',
        };

        this.cardContainer = this.closest(this.selectors.cardContainer);
        this.cardProduct = this.querySelector(this.selectors.cardProduct);
        this.closeButton = this.querySelector(this.selectors.closeButton);
        this.pin = this.querySelector(this.selectors.pin);
        this.triggerMode = this.dataset.trigger || 'both';
        this.cardHome = this.cardProduct?.parentNode;
        this.cardNextSibling = this.cardProduct?.nextSibling;
        this.handlePinClick = this.handlePinClick.bind(this);
        this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleMouseover = this.init.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.close.bind(this);
      }

      connectedCallback() {
        this.init();
        this.pin?.addEventListener('click', this.handlePinClick);
        this.closeButton?.addEventListener('click', this.handleCloseButtonClick);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeydown);
        document.addEventListener('matchMobile', this.handleViewportChange);
        document.addEventListener('unmatchMobile', this.handleViewportChange);
        this.addEventListener('mouseover', this.handleMouseover);

        if (Shopify.designMode) {
          this.addEventListener('shopify:block:select', this.handleBlockSelect);
          this.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
        }
      }

      disconnectedCallback() {
        this.pin?.removeEventListener('click', this.handlePinClick);
        this.closeButton?.removeEventListener('click', this.handleCloseButtonClick);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleDocumentKeydown);
        document.removeEventListener('matchMobile', this.handleViewportChange);
        document.removeEventListener('unmatchMobile', this.handleViewportChange);
        this.removeEventListener('mouseover', this.handleMouseover);
        this.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
        this.deactivateMobilePopup();
      }

      init() {
        this.classes = '';
        if (this.cardProduct) {
          this.cardContainerOffsetX = this.cardContainer.getBoundingClientRect().left;
          this.cardContainerOffsetY = this.cardContainer.getBoundingClientRect().top;

          this.offsetX = this.getBoundingClientRect().left - this.cardContainerOffsetX;
          this.offsetY = this.getBoundingClientRect().top - this.cardContainerOffsetY;

          this.width = this.clientWidth;
          this.cardWidth = this.cardProduct.clientWidth;
          this.cardHeight = this.cardProduct.clientHeight;
          this.wrapperWidth = this.cardContainer.clientWidth;
          this.wrapperHeight = this.cardContainer.clientHeight;

          this.cardPosition = this.offsetY > this.cardHeight ? 'top' : 'bottom';

          if (this.offsetX + this.width > this.cardWidth) {
            this.cardPosition += '-left';
          } else if (this.wrapperWidth - this.offsetX > this.cardWidth) {
            this.cardPosition += '-right';
          }

          this.dataset.position = this.cardPosition;
        }
      }

      handlePinClick(event) {
        event.stopPropagation();
        if (this.triggerMode === 'hover' && this.supportsHover()) return;

        const shouldOpen = !this.classList.contains('is-active');

        this.cardContainer?.querySelectorAll('lookbook-icon.is-active').forEach((hotspot) => {
          if (hotspot !== this) hotspot.close();
        });

        if (shouldOpen) {
          this.open();
        } else {
          this.close();
        }
      }

      handleCloseButtonClick(event) {
        event.stopPropagation();
        this.close();
        this.pin?.focus();
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target) && !this.cardProduct?.contains(event.target)) this.close();
      }

      handleDocumentKeydown(event) {
        if (event.key === 'Escape' && this.classList.contains('is-active')) {
          this.close();
          this.pin?.focus();
          return;
        }

        if (event.key === 'Tab' && this.cardProduct?.classList.contains('is-mobile-popup')) {
          const focusableElements = Array.from(
            this.cardProduct.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
          ).filter((element) => element.offsetParent !== null);
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (!firstElement || !lastElement) return;

          if (event.shiftKey && (document.activeElement === firstElement || !this.cardProduct.contains(document.activeElement))) {
            event.preventDefault();
            lastElement.focus();
          } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }

      handleViewportChange() {
        this.deactivateMobilePopup();
        this.init();
        if (this.classList.contains('is-active') && this.isMobile()) this.activateMobilePopup();
      }

      isMobile() {
        return window.matchMedia('(max-width: 767.98px)').matches;
      }

      supportsHover() {
        return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      }

      activateMobilePopup() {
        if (!this.cardProduct || !this.cardContainer || !this.isMobile()) return;

        this.cardContainer.appendChild(this.cardProduct);
        this.cardProduct.classList.add('is-mobile-popup');
        this.cardProduct.setAttribute('role', 'dialog');
        this.cardProduct.setAttribute('aria-modal', 'true');
        this.cardProduct.setAttribute('aria-label', this.pin?.getAttribute('aria-label') || '');
        this.cardContainer.classList.add('has-mobile-hotspot-popup');
        cancelAnimationFrame(this.focusFrame);
        this.focusFrame = requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
      }

      deactivateMobilePopup() {
        if (!this.cardProduct) return;

        cancelAnimationFrame(this.focusFrame);
        this.cardProduct.classList.remove('is-mobile-popup');
        this.cardProduct.removeAttribute('role');
        this.cardProduct.removeAttribute('aria-modal');
        this.cardProduct.removeAttribute('aria-label');

        if (this.cardHome && this.cardProduct.parentNode !== this.cardHome) {
          this.cardHome.insertBefore(this.cardProduct, this.cardNextSibling);
        }

        if (!this.cardContainer?.querySelector('.lookbook-icon__card.is-mobile-popup')) {
          this.cardContainer?.classList.remove('has-mobile-hotspot-popup');
        }
      }

      handleBlockSelect() {
        if (this.cardProduct) this.cardProduct.style.transition = 'none';
        this.open();
      }

      open() {
        this.init();
        this.classList.add('is-active');
        this.pin?.setAttribute('aria-expanded', 'true');
        if (this.isMobile()) this.activateMobilePopup();
      }

      close() {
        this.classList.remove('is-active');
        this.pin?.setAttribute('aria-expanded', 'false');
        this.deactivateMobilePopup();
      }
    }
  );
}
